"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, Database, RadioTower, RefreshCw, Server, Wifi } from "lucide-react";

import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { getRuntimeHealth, getSystemHealthOverview, type RuntimeHealthCheckDto } from "@/lib/api/endpoints/system-health";
import { cn } from "@/lib/utils/cn";
import { normalizeStatusTone, statusToneClasses, type StatusTone } from "@/lib/theme/status-tone";

function taskLabel(name: string): string {
  return name
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function taskIcon(name: string) {
  if (name.includes("mqtt")) return RadioTower;
  if (name.includes("websocket")) return Wifi;
  if (name.includes("buffer") || name.includes("ingest")) return Activity;
  return Server;
}

function statusPillClass(status?: string): string {
  const tone = normalizeStatusTone(status);
  const map: Record<StatusTone, string> = {
    healthy: "status-pill-healthy",
    warning: "status-pill-warning",
    unhealthy: "status-pill-unhealthy",
    neutral: "status-pill-neutral",
    info: "status-pill-info",
    offline: "status-pill-neutral",
    maintenance: "status-pill-warning",
  };
  return map[tone];
}

function StatusBadge({ status }: { status?: string }) {
  return <span className={statusPillClass(status)}>{status ?? "unknown"}</span>;
}

function HealthCard({
  title,
  status,
  detail,
  icon: Icon,
}: {
  title: string;
  status?: string;
  detail?: string;
  icon: typeof Activity;
}) {
  return (
    <div className="admin-surface rounded-lg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-lg border border-card-border bg-muted-bg text-foreground">
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <div className="text-sm font-semibold text-foreground">{title}</div>
            <div className="mt-1 text-xs text-muted">{detail || "Runtime probe is reporting normally."}</div>
          </div>
        </div>
        <StatusBadge status={status} />
      </div>
    </div>
  );
}

function flattenChecks(database: RuntimeHealthCheckDto | undefined, tasks: Record<string, RuntimeHealthCheckDto>) {
  const rows = database ? [{ key: "database", label: "Database", icon: Database, ...database }] : [];
  return [
    ...rows,
    ...Object.entries(tasks).map(([key, value]) => ({
      key,
      label: taskLabel(key),
      icon: taskIcon(key),
      ...value,
    })),
  ];
}

export function SystemHealthPage() {
  const runtimeQuery = useQuery({
    queryKey: ["runtime-health"],
    queryFn: getRuntimeHealth,
    refetchInterval: 15_000,
  });
  const overviewQuery = useQuery({
    queryKey: ["system-health"],
    queryFn: getSystemHealthOverview,
    refetchInterval: 30_000,
  });

  const database = runtimeQuery.data?.checks.database;
  const backgroundTasks = runtimeQuery.data?.checks.background_tasks ?? {};
  const runtimeChecks = useMemo(() => flattenChecks(database, backgroundTasks), [backgroundTasks, database]);

  const runtimeStats = useMemo(() => {
    const healthy = runtimeChecks.filter((check) => normalizeStatusTone(check.status) === "healthy").length;
    const warning = runtimeChecks.filter((check) => normalizeStatusTone(check.status) === "warning").length;
    const unhealthy = runtimeChecks.filter((check) => normalizeStatusTone(check.status) === "unhealthy").length;
    return { healthy, warning, unhealthy, total: runtimeChecks.length };
  }, [runtimeChecks]);

  const overviewStats = useMemo(() => {
    const points = overviewQuery.data?.ingest_points ?? [];
    const services = overviewQuery.data?.services ?? [];
    const maxValue = points.length ? Math.max(...points.map((point) => point.sample_value)) : 0;
    const avgValue = points.length
      ? Math.round(points.reduce((sum, point) => sum + point.sample_value, 0) / points.length)
      : 0;
    const healthy = services.filter((service) => service.status === "healthy").length;
    return { maxValue, avgValue, healthy };
  }, [overviewQuery.data?.ingest_points, overviewQuery.data?.services]);

  return (
    <EnterprisePageShell
      // kicker="Platform"
      title="System health"
      // description="Runtime health checks for API, database, and device listener workers."
    >
      <div className="grid gap-5 pt-5">
        <section className="admin-surface rounded-lg p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-foreground">Live runtime status</div>
              <p className="mt-1 text-sm text-muted">Backed by the FastAPI /health probe and refreshed every 15 seconds.</p>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge status={runtimeQuery.data?.status ?? (runtimeQuery.isError ? "unhealthy" : "loading")} />
              <button
                type="button"
                onClick={() => runtimeQuery.refetch()}
                className="inline-flex h-9 items-center gap-2 rounded-control border border-card-border bg-surface-primary px-3 text-caption font-semibold text-foreground transition-colors hover:bg-surface-hover"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>

          <div className="mt-5 grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            {[
              { label: "Healthy", value: runtimeStats.healthy, status: "healthy" },
              { label: "Warning", value: runtimeStats.warning, status: "not_started" },
              { label: "Unhealthy", value: runtimeStats.unhealthy, status: "unhealthy" },
              { label: "Checks", value: runtimeStats.total, status: runtimeQuery.data?.status ?? "unknown" },
            ].map((item) => (
              <div
                key={item.label}
                className={cn("rounded-lg border p-4", statusToneClasses(normalizeStatusTone(item.status)))}
              >
                <div className="text-xs font-bold uppercase">{item.label}</div>
                <div className="mt-2 text-2xl font-semibold tabular-nums">{item.value}</div>
              </div>
            ))}
          </div>
        </section>

        {runtimeQuery.isLoading ? (
          <div className="rounded-card border border-card-border bg-surface-primary p-4 text-caption text-muted" role="status">Loading runtime health...</div>
        ) : runtimeQuery.isError ? (
          <div className="rounded-lg border border-error/30 bg-error/10 p-4 text-sm text-error">
            {runtimeQuery.error instanceof Error ? runtimeQuery.error.message : "Failed to load runtime health."}
          </div>
        ) : (
          <section className="grid gap-3 lg:grid-cols-2">
            {runtimeChecks.map((check) => (
              <HealthCard
                key={check.key}
                title={check.label}
                status={check.status}
                detail={typeof check.detail === "string" ? check.detail : undefined}
                icon={check.icon}
              />
            ))}
          </section>
        )}

        <section className="grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="admin-surface rounded-lg p-5">
            <div className="text-sm font-semibold text-foreground">Ingest trend</div>
            <p className="mt-1 text-xs text-muted">Telemetry row counts grouped into recent 4-hour buckets.</p>
            <div className="mt-5 flex h-40 items-end gap-2 rounded-card border border-card-border bg-surface-secondary p-3">
              {(overviewQuery.data?.ingest_points ?? []).map((point) => (
                <div key={point.id} className="flex h-full flex-1 flex-col justify-end">
                  <div
                    title={`${point.sample_label}: ${point.sample_value}`}
                    className="rounded-t-md bg-primary"
                    style={{
                      height: `${overviewStats.maxValue ? Math.max(6, Math.round((point.sample_value / overviewStats.maxValue) * 100)) : 6}%`,
                    }}
                  />
                  <div className="mt-2 text-center font-mono text-[11px] text-muted">{point.sample_label}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <HealthCard title="Peak ingest" status="healthy" detail={`${overviewStats.maxValue} highest sample value`} icon={Activity} />
              <HealthCard title="Average ingest" status="healthy" detail={`${overviewStats.avgValue} average sample value`} icon={Activity} />
              <HealthCard title="Tracked services" status="healthy" detail={`${overviewStats.healthy} service checks currently healthy`} icon={Server} />
            </div>
          </div>

          <div className="admin-surface rounded-lg p-5">
            <div className="text-sm font-semibold text-foreground">Service records</div>
            <p className="mt-1 text-xs text-muted">Runtime and workspace health signals derived from live backend data. Optional fallback queues are labeled explicitly.</p>
            <div className="mt-4 grid gap-3">
              {(overviewQuery.data?.services ?? []).map((service) => (
                <div key={service.id} className="rounded-card border border-card-border bg-surface-primary p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold text-foreground">{service.service_name}</div>
                    <StatusBadge status={service.status} />
                  </div>
                  <div className="mt-2 text-lg font-semibold text-foreground">{service.value}</div>
                  <div className="mt-1 text-xs text-muted">{service.note}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="admin-surface overflow-hidden rounded-lg">
          <div className="border-b border-card-border bg-surface-secondary px-5 py-4">
            <div className="text-sm font-semibold text-foreground">Recent incidents</div>
            <p className="mt-1 text-xs text-muted">Recent alert activity surfaced as operator-facing incidents.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-surface-secondary">
                <tr>
                  {["Time", "Title", "Details", "Severity"].map((label) => (
                    <th key={label} className="border-b border-card-border px-4 py-3 text-left text-[11px] font-bold uppercase text-muted">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(overviewQuery.data?.incidents ?? []).length ? (
                  (overviewQuery.data?.incidents ?? []).map((incident) => (
                    <tr key={incident.id}>
                      <td className="border-b border-card-border px-4 py-3 text-xs text-muted">
                        {new Date(incident.occurred_at).toLocaleString()}
                      </td>
                      <td className="border-b border-card-border px-4 py-3 text-sm font-semibold text-foreground">{incident.title}</td>
                      <td className="border-b border-card-border px-4 py-3 text-sm text-muted">{incident.description}</td>
                      <td className="border-b border-card-border px-4 py-3">
                        <StatusBadge status={incident.severity} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted">
                      No incidents found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </EnterprisePageShell>
  );
}
